import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate } from "react-router-dom";
import Switch from "../../components/Switch";
import useApiUrls from "../../useApiUrls";
import Pagination from "../components/Pagination";

const ThirdPartyHtmlAllocation = () => {
  const [data, setData] = useState([]);
  const [htmlOptions, setHtmlOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [activeFilter, setActiveFilter] = useState("");
  const [dataLimit, setDataLimit] = useState(100);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");

  // Modal State for Add Allocation
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedHtmlId, setSelectedHtmlId] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [allocationActive, setAllocationActive] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Modal State for Delete Allocation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();

  const token = getCookie("token") || getCookie("authToken") || "";
  const userid = getCookie("userid") || getCookie("userId") || "1";

  const getHeaders = () => ({
    "accept": "text/plain",
    "UserId": userid,
    "X-Authorization": token,
    "Content-Type": "application/json-patch+json"
  });

  const showToast = (msg, type = "success") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
    }, 3000);
  };

  // Fetch Third Party HTML options from GetDistinct API
  const fetchHtmlOptions = useCallback(async () => {
    try {
      const getDistinctUrl = getUrlByOperation("ThirdPartyHtmlGetDistinctNameActive") || 
        "https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlOperation/GetDistinct/name_active";
      const response = await axios.get(getDistinctUrl, { headers: getHeaders() });
      if (response.data && Array.isArray(response.data.data)) {
        setHtmlOptions(response.data.data);
      } else if (response.data && Array.isArray(response.data)) {
        setHtmlOptions(response.data);
      }
    } catch (error) {
      console.error("Error fetching Third Party HTML distinct list:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUrlByOperation, token, userid]);

  // Fetch SubUsers options
  const fetchUserOptions = useCallback(async () => {
    try {
      const response = await axios.post(
        "https://api.dqr-display-tms.bonrix.in/api/SubUserOperation/Search",
        {
          id: 0,
          applycreateddate: false,
          applymodifieddate: false,
          fromcreateddate: 0,
          endcreateddate: 0,
          frommodifieddate: 0,
          endmodifieddate: 0,
          active: "",
          catalogname: "",
          ispageload: false,
          pagesize: 1000,
          currentpageno: 0,
          withoutpagination: true,
          pagedirection: ""
        },
        { headers: getHeaders() }
      );
      if (response.data && Array.isArray(response.data.data)) {
        setUserOptions(response.data.data);
      } else if (response.data && Array.isArray(response.data)) {
        setUserOptions(response.data);
      }
    } catch (error) {
      console.error("Error fetching subusers list:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userid]);

  // Fetch Allocations
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const searchUrl = getUrlByOperation("ThirdPartyHtmlAllocationSearch") || 
        "https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlAllocationOperation/Search";
      const response = await axios.post(
        searchUrl,
        {
          id: -1,
          createdip: "",
          modifiedip: "",
          applycreateddate: false,
          applymodifieddate: false,
          fromcreateddate: 0,
          endcreateddate: 0,
          frommodifieddate: 0,
          endmodifieddate: 0,
          active: activeFilter,
          ispageload: true,
          pagesize: dataLimit,
          currentpageno: count,
          withoutpagination: false,
          pagedirection: ""
        },
        { headers: getHeaders() }
      );

      if (response.data && Array.isArray(response.data.data)) {
        const rows = response.data.data;
        setData(rows);
        setHasMore(rows.length === Number(dataLimit));
        const approximateTotal = (count * Number(dataLimit)) + rows.length + (rows.length === Number(dataLimit) ? Number(dataLimit) : 0);
        setTotalItems(approximateTotal);
      } else {
        setData([]);
        setHasMore(false);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching allocation data:", error);
      showToast(error.response?.data?.errormsg || "Failed to load allocation data", "danger");
      if (error.message === "relogin token expire" || error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUrlByOperation, token, userid, dataLimit, count, activeFilter, navigate]);

  useEffect(() => {
    setLoadingOptions(true);
    Promise.all([fetchHtmlOptions(), fetchUserOptions()]).finally(() => {
      setLoadingOptions(false);
    });
  }, [fetchHtmlOptions, fetchUserOptions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (page) => {
    setCount(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setDataLimit(newItemsPerPage);
    setCount(0);
  };

  const totalPages = hasMore ? count + 2 : count + 1;

  const handleAddClick = () => {
    setSelectedHtmlId("");
    setSelectedUserId("");
    setAllocationActive(true);
    setShowAddModal(true);
  };

  const handleSaveAllocation = async (e) => {
    e.preventDefault();
    if (!selectedHtmlId) {
      showToast("Please select a Third Party HTML item", "danger");
      return;
    }
    if (!selectedUserId) {
      showToast("Please select a User", "danger");
      return;
    }

    setIsSaving(true);
    try {
      const insertUrl = getUrlByOperation("ThirdPartyHtmlAllocationInsert") || 
        "https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlAllocationOperation/Insert";
      const payload = {
        active: Boolean(allocationActive),
        thirdpartyhtmlid: parseInt(selectedHtmlId),
        subuserid: parseInt(selectedUserId)
      };

      const response = await axios.post(insertUrl, payload, { headers: getHeaders() });
      if (response.data && (response.data.isresponse || response.data.responsestatus === "SUCCESS")) {
        showToast("Allocation added successfully!", "success");
        setShowAddModal(false);
        fetchData();
      } else {
        showToast(response.data?.errormsg || "Failed to add allocation", "danger");
      }
    } catch (error) {
      console.error("Error inserting allocation:", error);
      showToast(error.response?.data?.errormsg || "Failed to add allocation", "danger");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedItem) return;
    setIsDeleting(true);
    try {
      const deleteUrl = getUrlByOperation("ThirdPartyHtmlAllocationDelete") || 
        "https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlAllocationOperation/Delete";
      const response = await axios.post(
        deleteUrl,
        { id: selectedItem.id },
        { headers: getHeaders() }
      );

      if (response.data && (response.data.isresponse || response.data.responsestatus === "SUCCESS")) {
        showToast("Allocation deleted successfully!", "success");
        setShowDeleteModal(false);
        fetchData();
      } else {
        showToast(response.data?.errormsg || "Failed to delete allocation", "danger");
      }
    } catch (error) {
      console.error("Error deleting allocation:", error);
      showToast(error.response?.data?.errormsg || "Failed to delete allocation", "danger");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggle = async (item) => {
    const activeUrl = getUrlByOperation("ThirdPartyHtmlAllocationActive") || 
      "https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlAllocationOperation/UpdateStatus/Active";
    const deactiveUrl = getUrlByOperation("ThirdPartyHtmlAllocationDeactive") || 
      "https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlAllocationOperation/UpdateStatus/Deactive";
    const apiUrl = item.active ? deactiveUrl : activeUrl;

    try {
      await axios.post(apiUrl, { id: item.id }, { headers: getHeaders() });
      setData(data.map((d) => (d.id === item.id ? { ...d, active: !item.active } : d)));
      showToast(`Allocation status updated to ${!item.active ? "Active" : "Inactive"}`, "success");
    } catch (error) {
      console.error("Error updating allocation status:", error);
      showToast(error.response?.data?.errormsg || "Error updating status", "danger");
    }
  };

  const getHtmlName = (htmlId, defaultName) => {
    if (defaultName) return defaultName;
    const found = htmlOptions.find((opt) => String(opt.id || opt.value) === String(htmlId));
    return found ? (found.name || found.value || found.htmlname || `HTML #${htmlId}`) : `HTML #${htmlId}`;
  };

  const getUserName = (subUserId, defaultUserName) => {
    if (defaultUserName) return defaultUserName;
    const found = userOptions.find((opt) => String(opt.id) === String(subUserId));
    return found ? (found.username || found.name || found.email || `User #${subUserId}`) : `User #${subUserId}`;
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          {/* Header */}
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100">
                <h1 className="mb-0">Third Party HTML Allocation</h1>
                <div>
                  <button
                    type="button"
                    className="btn btn-secondary mr-2"
                    onClick={() => navigate("/thirdparty-html")}
                  >
                    <i className="fas fa-list mr-1"></i> Third Party HTML List
                  </button>
                  <button type="button" className="btn btn-success" onClick={handleAddClick}>
                    <i className="fas fa-plus mr-1"></i> Add Allocation
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <section className="content">
            <div className="container-fluid">
              <hr />

              {/* Filters */}
              <div className="card mb-3">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4 col-sm-6 mb-2">
                      <select
                        className="form-control"
                        value={activeFilter}
                        onChange={(e) => {
                          setActiveFilter(e.target.value);
                          setCount(0);
                        }}
                      >
                        <option value="">All Statuses</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="col-md-4 col-sm-6 mb-2 d-flex">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setActiveFilter("");
                          setCount(0);
                        }}
                      >
                        Reset Filter
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body table-responsive p-0">
                      <table className="table table-hover text-nowrap">
                        <thead>
                          <tr>
                            <th>Id</th>
                            <th>Third Party HTML</th>
                            <th>User</th>
                            <th>Active</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="5" className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                  <span className="sr-only">Loading...</span>
                                </div>
                              </td>
                            </tr>
                          ) : data.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center py-4">
                                No allocation records found.
                              </td>
                            </tr>
                          ) : (
                            data.map((item) => (
                              <tr key={item.id}>
                                <td>{item.id}</td>
                                <td>
                                  <strong>{getHtmlName(item.thirdpartyhtmlid, item.thirdpartyhtmlname || item.name)}</strong>
                                  <span className="text-muted small ml-1">(ID: {item.thirdpartyhtmlid})</span>
                                </td>
                                <td>
                                  <strong>{getUserName(item.subuserid, item.username || item.subusername)}</strong>
                                  <span className="text-muted small ml-1">(ID: {item.subuserid})</span>
                                </td>
                                <td>
                                  <Switch
                                    isOn={Boolean(item.active)}
                                    handleToggle={() => handleToggle(item)}
                                    id={item.id}
                                  />
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDeleteClick(item)}
                                    title="Delete Allocation"
                                  >
                                    <i className="fas fa-trash"></i> Delete
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>

                      <Pagination
                        currentPage={count + 1}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        itemsPerPage={dataLimit}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        totalItems={totalItems}
                        showPageNumbers={false}
                        showItemsPerPageSelector={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Add Allocation Modal */}
      {showAddModal && (
        <div className="modal" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">Add Third Party HTML Allocation</h5>
                <button type="button" className="close text-white" onClick={() => setShowAddModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <form onSubmit={handleSaveAllocation}>
                <div className="modal-body">
                  {loadingOptions ? (
                    <div className="text-center py-3">
                      <div className="spinner-border text-info" role="status">
                        <span className="sr-only">Loading options...</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Third Party HTML Dropdown */}
                      <div className="form-group mb-3">
                        <label className="font-weight-bold">Third Party HTML *</label>
                        <select
                          className="form-control"
                          value={selectedHtmlId}
                          onChange={(e) => setSelectedHtmlId(e.target.value)}
                          required
                        >
                          <option value="">-- Select Third Party HTML --</option>
                          {htmlOptions.map((opt) => {
                            const optId = opt.id || opt.value;
                            const optName = opt.name || opt.value || opt.htmlname || `HTML #${optId}`;
                            return (
                              <option key={optId} value={optId}>
                                {optName} (ID: {optId})
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      {/* User Dropdown */}
                      <div className="form-group mb-3">
                        <label className="font-weight-bold">User *</label>
                        <select
                          className="form-control"
                          value={selectedUserId}
                          onChange={(e) => setSelectedUserId(e.target.value)}
                          required
                        >
                          <option value="">-- Select User --</option>
                          {userOptions.map((user) => {
                            const userName = user.username || user.name || user.email || `User #${user.id}`;
                            return (
                              <option key={user.id} value={user.id}>
                                {userName} (ID: {user.id})
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      {/* Active Switch/Select */}
                      <div className="form-group mb-3">
                        <label className="font-weight-bold">Status</label>
                        <select
                          className="form-control"
                          value={allocationActive}
                          onChange={(e) => setAllocationActive(e.target.value === "true")}
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-info" disabled={isSaving || loadingOptions}>
                    {isSaving ? "Saving..." : "Save Allocation"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">Confirm Delete Allocation</h5>
                <button type="button" className="close text-white" onClick={() => setShowDeleteModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body text-center">
                <p>Are you sure you want to delete allocation <strong>ID #{selectedItem?.id}</strong>?</p>
                <p className="text-muted"><small>This action cannot be undone.</small></p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={isDeleting}>
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {message && (
        <div style={{ position: "fixed", top: "20px", right: "20px", width: "300px", zIndex: "9999" }}>
          <div className={`alert alert-${messageType} alert-dismissible fade show`} role="alert">
            {message}
            <button type="button" className="close" onClick={() => setMessage("")}>
              <span>&times;</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ThirdPartyHtmlAllocation;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}
